import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { DeliverableTemplate } from '@horwath-htl/shared-types';

interface TemplateEditDialogProps {
  open: boolean;
  onClose: () => void;
  template: DeliverableTemplate;
  onSuccess?: () => void;
}

interface UpdateRequest {
  name?: string;
  description?: string;
  is_active?: boolean;
}

export const TemplateEditDialog: React.FC<TemplateEditDialogProps> = ({
  open,
  onClose,
  template,
  onSuccess,
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  // Initialize form with template data when dialog opens
  useEffect(() => {
    if (open && template) {
      setName(template.name);
      setDescription(template.description || '');
      setIsActive(template.is_active);
    }
  }, [open, template]);

  const updateMutation = useMutation({
    mutationFn: async () => {
      const updateData: UpdateRequest = {
        name,
        description,
        is_active: isActive,
      };

      const response = await authenticatedFetch(
        `/api/deliverables/templates/${template.id}`,
        {
          method: 'PUT',
          body: JSON.stringify(updateData),
        }
      );

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Update failed');
      }

      return await response.json();
    },
    onSuccess: () => {
      // Invalidate queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['templates'] });
      queryClient.invalidateQueries({ queryKey: ['template', template.id] });

      if (onSuccess) {
        onSuccess();
      }
      handleClose();
    },
  });

  const handleClose = () => {
    if (!updateMutation.isPending) {
      onClose();
    }
  };

  const handleSave = () => {
    updateMutation.mutate();
  };

  const canSave = name.trim().length > 0 && name.length <= 255;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Template</DialogTitle>
      <DialogContent>
        {/* Template Name */}
        <TextField
          fullWidth
          label="Template Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={updateMutation.isPending}
          error={name.length > 255}
          helperText={`${name.length}/255 characters`}
          sx={{ mt: 2, mb: 2 }}
        />

        {/* Description */}
        <TextField
          fullWidth
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          multiline
          rows={4}
          disabled={updateMutation.isPending}
          sx={{ mb: 2 }}
        />

        {/* Active Status */}
        <FormControlLabel
          control={
            <Checkbox
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={updateMutation.isPending}
            />
          }
          label={
            <Typography variant="body2">
              Active - Template is available for use in deliverables
            </Typography>
          }
        />

        {/* Error Display */}
        {updateMutation.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {updateMutation.error instanceof Error
              ? updateMutation.error.message
              : 'An error occurred while updating the template'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={updateMutation.isPending}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!canSave || updateMutation.isPending}
          startIcon={updateMutation.isPending ? <CircularProgress size={20} /> : null}
        >
          {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
